import { Component, BaseOptions } from '../core/component';

const _defaults = Object.freeze({});

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export class CharacterCounter extends Component<BaseOptions> {
  declare el: InputElement;
  /** Stores the reference to the counter HTML element. */
  counterEl: HTMLSpanElement;
  /** Specifies whether the input is valid or not. */
  isInvalid: boolean;
  /** Specifies whether the input text has valid length or not. */
  isValidLength: boolean;

  constructor(el: HTMLInputElement | HTMLTextAreaElement, options: Partial<BaseOptions>) {
    super(el, options);

    this.isInvalid = false;
    this.isValidLength = false;

    this._setupCounter();
    this._setupEventHandlers();
    // A prefilled field shows its count and over-length state before focus.
    this.updateCounter();
  }

  static get defaults(): BaseOptions {
    return _defaults;
  }

  static key = 'Expressive_CharacterCounter';

  destroy() {
    super.destroy();
    this._removeCounter();
    if (this.isInvalid) this.el.classList.remove('invalid');
  }

  _setupEventHandlers() {
    const options = { capture: true, signal: this._events.signal };
    this.el.addEventListener('focus', this.updateCounter, options);
    this.el.addEventListener('input', this.updateCounter, options);
  }

  _setupCounter() {
    this.counterEl = document.createElement('span');
    this.counterEl.classList.add('character-counter');
    // The count is the whole point of the component and it changes as the
    // user types, so it has to be a live region - otherwise a screen reader
    // user reaches the limit without ever being told one existed. Polite so
    // it waits for a pause instead of interrupting every keystroke, and
    // atomic so "18/20" is read as one figure rather than a changed digit.
    this.counterEl.setAttribute('aria-live', 'polite');
    this.counterEl.setAttribute('aria-atomic', 'true');
    this.counterEl.style.fontSize = '12px';
    this.counterEl.style.height = '1';
    this.el.parentElement.appendChild(this.counterEl);
  }

  _removeCounter() {
    this.counterEl.remove();
  }

  updateCounter = () => {
    const maxLength = parseInt(this.el.getAttribute('maxlength')),
      actualLength = this.el.value.length;

    this.isValidLength = actualLength <= maxLength;
    let counterString = actualLength.toString();
    if (maxLength) {
      counterString += '/' + maxLength;
      this._validateInput();
    }
    this.counterEl.textContent = counterString;
  };

  _validateInput() {
    if (this.isValidLength && this.isInvalid) {
      this.isInvalid = false;
      this.el.classList.remove('invalid');
    } else if (!this.isValidLength && !this.isInvalid) {
      this.isInvalid = true;
      this.el.classList.remove('valid');
      this.el.classList.add('invalid');
    }
  }
}
